import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Search, UserPlus, X } from 'lucide-react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { api } from '../../lib/api';

interface AccountMember {
  user_id: string;
  email: string;
  display_name: string | null;
}

interface ProjectMembersSnapshot {
  owner_id: string;
  members: { user_id: string }[];
}

export function AddProjectMemberModal({
  open,
  onClose,
  projectId,
  tenantId,
}: {
  open: boolean;
  onClose: () => void;
  projectId: string;
  tenantId: string;
}) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [confirmAdd, setConfirmAdd] = useState<AccountMember | null>(null);

  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.get<ProjectMembersSnapshot>(`/projects/${projectId}`),
    enabled: open && !!projectId,
    staleTime: 10_000,
  });

  const { data: accountMembers = [], isLoading: membersLoading } = useQuery({
    queryKey: ['members', tenantId],
    queryFn: () => api.get<AccountMember[]>(`/accounts/${tenantId}/members`),
    enabled: open && !!tenantId,
    staleTime: 30_000,
  });

  const isLoading = projectLoading || membersLoading;

  const onProject = useMemo(() => {
    const ids = new Set(project?.members.map((m) => m.user_id) ?? []);
    if (project?.owner_id) ids.add(project.owner_id);
    return ids;
  }, [project]);

  const eligible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return accountMembers
      .filter((m) => !onProject.has(m.user_id))
      .filter((m) => {
        if (!q) return true;
        const name = (m.display_name ?? m.email).toLowerCase();
        return name.includes(q) || m.email.toLowerCase().includes(q);
      })
      .sort((a, b) => (a.display_name ?? a.email).localeCompare(b.display_name ?? b.email));
  }, [accountMembers, onProject, search]);

  const addMember = useMutation({
    mutationFn: (email: string) => api.post(`/projects/${projectId}/members`, { email }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project', projectId] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Member added');
      setConfirmAdd(null);
      onClose();
    },
    onError: (e: Error) => {
      toast.error(e.message ?? 'Failed to add member');
    },
  });

  if (!open) return null;

  const handleClose = () => {
    if (addMember.isPending) return;
    setSearch('');
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      onClick={handleClose}
      role="presentation"
    >
      <div
        className="flex max-h-[min(520px,85vh)] w-full max-w-md flex-col overflow-hidden rounded-2xl
                   border border-surface-3 bg-surface-1 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-project-member-title"
      >
        <div className="flex items-start justify-between gap-3 border-b border-surface-3 px-5 py-4">
          <div>
            <h2 id="add-project-member-title" className="text-[15px] font-semibold text-fg">
              Add project member
            </h2>
            <p className="mt-0.5 text-[12px] text-fg-3 leading-relaxed">
              People in your account who are not already on this project.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-fg-3
                       hover:bg-surface-3 hover:text-fg"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="border-b border-surface-3 px-5 py-3">
          <div className="relative">
            <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-3" />
            <input
              autoFocus
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email"
              className="input-field w-full pl-9 text-[13px]"
            />
          </div>
        </div>

        <div className="min-h-[200px] flex-1 overflow-y-auto px-2 py-2">
          {isLoading ? (
            <div className="flex h-40 items-center justify-center">
              <Loader2 size={20} className="animate-spin text-fg-3" />
            </div>
          ) : eligible.length === 0 ? (
            <p className="px-3 py-8 text-center text-[13px] text-fg-3">
              {accountMembers.length === 0
                ? 'No account members found.'
                : search.trim()
                  ? 'No matching people outside this project.'
                  : 'Everyone in the account is already on this project.'}
            </p>
          ) : (
            <ul className="divide-y divide-surface-3/60">
              {eligible.map((m) => {
                const label = m.display_name?.trim() || m.email;
                return (
                  <li key={m.user_id}>
                    <button
                      type="button"
                      disabled={addMember.isPending}
                      onClick={() => setConfirmAdd(m)}
                      className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left
                                 hover:bg-surface-2 disabled:opacity-50 transition-colors"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full
                                       bg-brand-500/12 text-[12px] font-bold text-brand-600">
                        {label.charAt(0).toUpperCase()}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-medium text-fg">{label}</span>
                        {m.display_name && (
                          <span className="block truncate text-[11.5px] text-fg-3">{m.email}</span>
                        )}
                      </span>
                      <UserPlus size={14} className="shrink-0 text-fg-3" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex justify-end border-t border-surface-3 px-5 py-3">
          <Button type="button" size="sm" variant="ghost" onClick={handleClose} disabled={addMember.isPending}>
            Cancel
          </Button>
        </div>
      </div>

      <AlertDialog
        open={confirmAdd !== null}
        onOpenChange={(open) => {
          if (!open && !addMember.isPending) setConfirmAdd(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Add {confirmAdd?.display_name?.trim() || confirmAdd?.email} to this project?
            </AlertDialogTitle>
            <AlertDialogDescription>
              They will be able to access this project&apos;s workflows and content as a member.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={addMember.isPending}>Cancel</AlertDialogCancel>
            <Button
              type="button"
              size="sm"
              disabled={addMember.isPending || !confirmAdd}
              onClick={() => {
                if (confirmAdd) addMember.mutate(confirmAdd.email);
              }}
            >
              {addMember.isPending ? 'Adding…' : 'Add member'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
